import { BaseVideoConfig, LayoutVariant } from '../config'

export type EngineId = 'ffmpeg' | 'mediabunny'

export type EngineLabel = {
  id: EngineId
  label: string
  description: string
}

export const ENGINES: EngineLabel[] = [
  {
    id: 'ffmpeg',
    label: 'FFmpeg WASM',
    description: 'Client-side render via FFmpeg compiled to WebAssembly.',
  },
  {
    id: 'mediabunny',
    label: 'Mediabunny',
    description: 'Client-side render via WebCodecs + Mediabunny muxer.',
  },
]

// The engine that actually runs. 'ffmpeg-st' is the scalar single-thread core
// used on browsers without WASM SIMD (iOS/Safari < 16.4).
export type ActualEngine = 'mediabunny' | 'ffmpeg' | 'ffmpeg-st'

export type RenderInput = {
  baseVideo: BaseVideoConfig
  userBlob: Blob
  overlay: File | null
  overlayImageSrc?: string
  layout: LayoutVariant
  durationSeconds: number
  onProgress?: (ratio: number, phase?: string) => void
  onLog?: (msg: string) => void
  // Reports which engine actually executed (after any fallback), so callers can
  // log/report the real engine instead of the initially-chosen one.
  onEngine?: (engine: ActualEngine) => void
}

export type RenderFn = (input: RenderInput) => Promise<Blob>

const STORAGE_KEY = 'sbv:engine'

// WebCodecs required for Mediabunny: VideoEncoder + VideoDecoder + AudioEncoder
// + AudioDecoder + OffscreenCanvas. Safari < 16.4 and Firefox < 130 are missing.
export const isWebCodecsSupported = (): boolean => {
  if (typeof window === 'undefined') return false
  return (
    typeof (window as any).VideoEncoder !== 'undefined' &&
    typeof (window as any).VideoDecoder !== 'undefined' &&
    typeof (window as any).AudioEncoder !== 'undefined' &&
    typeof (window as any).AudioDecoder !== 'undefined' &&
    typeof OffscreenCanvas !== 'undefined'
  )
}

// WASM SIMD (v128) detection. The ffmpeg-core 0.12.x build declares v128
// locals, so a browser without SIMD throws at compile time:
//   "CompileError: ... can't get Function local's type in group 0"
// Safari/iOS shipped SIMD in 16.4 — the same release that added WebCodecs — so
// below 16.4 BOTH engines are dead. Bytes are the canonical wasm-feature-detect
// SIMD probe (a function returning a v128).
export const isWasmSimdSupported = (): boolean => {
  if (typeof WebAssembly === 'undefined' || typeof WebAssembly.validate !== 'function') {
    return false
  }
  try {
    return WebAssembly.validate(
      new Uint8Array([
        0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10,
        1, 8, 0, 65, 0, 253, 15, 253, 98, 11,
      ]),
    )
  } catch {
    return false
  }
}

// WASM bulk-memory detection. The scalar core-st 0.11 build uses bulk-memory
// ops, which emit a DataCount section (wasm section id 12). iOS/Safari < 15
// predates that section and fails to even parse the module:
//   "CompileError: WebAssembly.Module doesn't parse ... invalid section"
// Bulk-memory landed in Safari/iOS 15.0, so this is the real floor for the
// scalar render path. Bytes are the wasm-feature-detect bulk-memory probe (a
// memory.copy on a single page of memory).
export const isWasmBulkMemorySupported = (): boolean => {
  if (typeof WebAssembly === 'undefined' || typeof WebAssembly.validate !== 'function') {
    return false
  }
  try {
    return WebAssembly.validate(
      new Uint8Array([
        0, 97, 115, 109, 1, 0, 0, 0, 1, 4, 1, 96, 0, 0, 3, 2, 1, 0, 5, 3, 1, 0,
        1, 10, 14, 1, 12, 0, 65, 0, 65, 0, 65, 0, 252, 10, 0, 0, 11,
      ]),
    )
  } catch {
    return false
  }
}

// Gate for the step entry. Render runs entirely client-side and every path
// needs at least bulk-memory: Mediabunny on WebCodecs (modern), the SIMD ffmpeg
// core (>= 16.4), or the scalar core-st (>= 15). iOS <= 14 has none of these and
// can't even compile the core wasm, so it's blocked with a clear message.
export const isRenderSupported = (): boolean =>
  isWebCodecsSupported() || isWasmBulkMemorySupported()

export const getStoredEngine = (): EngineId => {
  if (typeof window === 'undefined') return 'ffmpeg'
  const supported = isWebCodecsSupported()
  const stored = window.localStorage.getItem(STORAGE_KEY)
  // Explicit override from engine picker — but never honor a 'mediabunny'
  // override on a browser without WebCodecs (e.g. iOS < 16.4). Mediabunny needs
  // OffscreenCanvas + VideoEncoder there; forcing it would throw mid-render
  // ("Something went wrong creating your video"). Fall back to ffmpeg instead.
  if (stored === 'ffmpeg') return 'ffmpeg'
  if (stored === 'mediabunny') return supported ? 'mediabunny' : 'ffmpeg'
  // Default: mediabunny if browser supports it, ffmpeg otherwise
  return supported ? 'mediabunny' : 'ffmpeg'
}

export const setStoredEngine = (id: EngineId): void => {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(STORAGE_KEY, id)
}

// Force the scalar single-thread path on any device (QA: test the old-iOS
// fallback from a modern browser). Set window.__SBV_FORCE_ST = true.
const forceScalar = (): boolean =>
  typeof window !== 'undefined' &&
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  !!(window as any).__SBV_FORCE_ST

// True when render() will take the scalar single-thread core-st path (forced,
// or ffmpeg engine on a browser without WASM SIMD). That path runs on the main
// thread and blocks repaints, so the UI shows an indeterminate spinner instead
// of a % bar. Mirrors the branch logic in render() exactly.
export const willUseScalar = (): boolean => {
  const id = getStoredEngine()
  if (id === 'mediabunny' && !forceScalar()) return false
  return forceScalar() || !isWasmSimdSupported()
}

// Mediabunny muxes AAC audio via WebCodecs AudioEncoder. Some browsers expose
// WebCodecs (so isWebCodecsSupported() is true) yet cannot encode AAC — notably
// Firefox, which throws "encoder configuration (mp4a.40.2 …) is not supported".
// Probe it so we route those browsers to ffmpeg BEFORE wasting a render pass.
const canEncodeAac = async (): Promise<boolean> => {
  if (typeof window === 'undefined') return true
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const AE = (window as any).AudioEncoder
  if (!AE || typeof AE.isConfigSupported !== 'function') return true // can't probe → assume ok
  try {
    const res = await AE.isConfigSupported({
      codec: 'mp4a.40.2',
      sampleRate: 44100,
      numberOfChannels: 2,
      bitrate: 128000,
    })
    return !!(res && res.supported)
  } catch {
    return false
  }
}

export const render: RenderFn = async input => {
  const id = getStoredEngine()

  const ffmpegInput = {
    baseSrc: input.baseVideo.src,
    userBlob: input.userBlob,
    overlay: input.overlay,
    overlayImageSrc: input.overlayImageSrc,
    layout: input.layout,
    durationSeconds: input.durationSeconds,
    onProgress: input.onProgress,
    onLog: input.onLog,
  }

  // ffmpeg path. The default 0.12 core needs WASM SIMD; browsers without it
  // (iOS/Safari < 16.4) must use the scalar single-thread core (0.11 +
  // core-st), which is the only ffmpeg build that loads there.
  const runFfmpeg = async () => {
    if (forceScalar() || !isWasmSimdSupported()) {
      input.onEngine?.('ffmpeg-st')
      const { composite } = await import('./composite-st')
      return composite(ffmpegInput)
    }
    input.onEngine?.('ffmpeg')
    const { composite } = await import('./composite')
    return composite(ffmpegInput)
  }

  if (id === 'mediabunny' && !forceScalar() && (await canEncodeAac())) {
    input.onEngine?.('mediabunny')
    try {
      const { composite } = await import('./mediabunny')
      return await composite(input)
    } catch (e) {
      // WebCodecs render failed at runtime (codec/config/decode). Don't hard
      // fail — fall back to the ffmpeg engine so the user still gets a video.
      if (input.onLog) {
        input.onLog(
          '[sbv] mediabunny failed, falling back to ffmpeg: ' +
            ((e as Error)?.message ?? String(e)),
        )
      }
      return runFfmpeg()
    }
  }

  return runFfmpeg()
}
